import { LandlordDTO, TenancyDTO, TenancyDTOTermTypeEnum, TenancyOrderDTO, TenancyOrderNoteDTO } from '@reposit/api-client';
import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { NumberFormat } from '../../../constants/number-formats';
import { TenantWithActions } from '../../../redux/selectors';
import { getLandlordFullName } from '../../../utils/common.utils';
import Button from '../../Button';
import CollapsableContent from '../../CollapsableContent';
import IdentifierValue from '../../IdentifierValue';
import InlineTable from '../../InlineTable';
import TenantList from '../../TenantList';
import { Header4, P1, P2 } from '../../Typography';
import RepositCard from '../RepositCard';

interface RepositSummaryCardProps {
  landlord?: LandlordDTO;
  notes?: TenancyOrderNoteDTO[];
  notesError?: string;
  tenancy: TenancyDTO;
  tenancyOrder: TenancyOrderDTO;
  tenantsWithActions?: TenantWithActions[];
}

const termTypeLabels: Record<TenancyDTOTermTypeEnum, string> = {
  [TenancyDTOTermTypeEnum.FIXED]: 'Fixed',
  [TenancyDTOTermTypeEnum.PERIODIC]: 'Periodic',
};

const CardSection = styled.div`
  padding: 24px 35px 0;
`;

const SectionTitle = styled(Header4)`
  margin-bottom: 12px;
`;

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Note = styled.div`
  background: #faf9fc;
  margin-bottom: 5px;
  padding: 16px 20px;
`;

const NoteText = styled(P1)`
  margin-bottom: 8px;
  white-space: pre-wrap;
`;

const NoteMetadata = styled(P2)`
  margin-bottom: 0;
`;

const OrganizationNotes = styled(P1)`
  margin-bottom: 0;
  white-space: pre-wrap;
`;

const ViewRepositLink = styled(NavLink)`
  margin-left: auto;
`;

const formatSwitch = (isNewTenancy?: boolean | null) => {
  if (isNewTenancy === true) return 'No';
  if (isNewTenancy === false) return 'Yes';
  return 'N/A';
};

const formatNoteMetadata = (note: TenancyOrderNoteDTO) => {
  const author = note.user && [note.user.firstName, note.user.lastName].filter(Boolean).join(' ');
  const createdAt = moment(note.createdAt).format('DD/MM/YYYY HH:mm');
  return author ? `${author}, ${createdAt}` : createdAt;
};

const RepositSummaryCard: React.FC<RepositSummaryCardProps> = ({
  landlord,
  notes,
  notesError,
  tenancy,
  tenancyOrder,
  tenantsWithActions,
}) => {
  const tenants = tenancy.tenants || [];
  const { notes: organizationNotes } = tenancy.organization.attributes || {};
  const displayedTenants: TenantWithActions[] = tenantsWithActions || tenants.map((tenant) => ({ ...tenant, actions: [] }));
  const items = [
    {
      key: 'ID',
      value: <IdentifierValue>{tenancyOrder.id}</IdentifierValue>,
    },
    {
      key: 'Start Date',
      value: tenancy.startDate ? moment(tenancy.startDate).format('DD/MM/YYYY') : 'N/A',
    },
    {
      key: 'Rent Per Month',
      value: typeof tenancy.PPM === 'number' ? `£${numeral(tenancy.PPM / 100).format(NumberFormat.THOUSANDS_PENCE)}` : 'N/A',
    },
    {
      key: 'Term Type',
      value: tenancy.termType ? termTypeLabels[tenancy.termType] : 'N/A',
    },
    {
      key: 'Reposit Switch',
      value: formatSwitch(tenancy.isNewTenancy),
    },
    {
      key: 'Landlord',
      value: landlord ? getLandlordFullName(landlord) || 'N/A' : 'N/A',
      linkTo: landlord ? `/landlords/${landlord.id}` : undefined,
    },
  ];

  return (
    <RepositCard
      flush
      title="Reposit Summary"
      headerContent={
        <ViewRepositLink to={`/reposits/${tenancyOrder.id}`}>
          <Button buttonType="secondary">View Reposit</Button>
        </ViewRepositLink>
      }
    >
      <Container>
        <Row>
          <Col lg={12} style={{ padding: 0 }}>
            <InlineTable items={items} />
          </Col>
        </Row>
      </Container>
      <CardSection>
        <SectionTitle>Tenants</SectionTitle>
        <CollapsableContent buttonTitle="Show tenants +" flushBottom openButtonTitle="Hide tenants -">
          {displayedTenants.length > 0 ? (
            <TenantList
              flush
              hideButtons
              tenants={displayedTenants}
              tenancyId={tenancy.id}
              tenancyOrderStatus={tenancyOrder.order.statusId}
            />
          ) : (
            <P2>N/A</P2>
          )}
        </CollapsableContent>
      </CardSection>
      <NotesGrid>
        <CardSection>
          <SectionTitle>Organisation Notes</SectionTitle>
          {organizationNotes ? (
            <OrganizationNotes>{organizationNotes}</OrganizationNotes>
          ) : (
            <P2>No organisation notes have been added.</P2>
          )}
        </CardSection>
        <CardSection>
          <SectionTitle>Reposit Notes</SectionTitle>
          {notesError ? (
            <P2>Reposit notes could not be loaded.</P2>
          ) : notes && notes.length > 0 ? (
            [...notes]
              .sort((noteA, noteB) => moment(noteA.createdAt).valueOf() - moment(noteB.createdAt).valueOf())
              .map((note) => (
                <Note key={note.id}>
                  <NoteText>{note.note}</NoteText>
                  <NoteMetadata>{formatNoteMetadata(note)}</NoteMetadata>
                </Note>
              ))
          ) : notes ? (
            <P2>No Reposit notes have been added.</P2>
          ) : (
            <P2>Loading Reposit notes...</P2>
          )}
        </CardSection>
      </NotesGrid>
    </RepositCard>
  );
};

export default RepositSummaryCard;
